import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Crown, User, RefreshCw, Search, Users, UserCheck, UserX, Info } from "lucide-react";

/**
 * Iter-70: Members & Roles — dedicated admin page (split out of Band Settings).
 *
 * Splits members into 4 tabs by player_type:
 *   - Members (player_type === "member")
 *   - Deps     (player_type === "dep")
 *   - Inactive (player_type === "inactive")
 *   - Info Only(player_type === "info_only")
 *
 * Each tab is sorted alphabetically by name. Admins can:
 *   - Promote/demote between admin ↔ player
 *   - Toggle Committee + Treasurer flags
 *   - Search across all four tabs at once
 *   - Clean up orphan login accounts
 *
 * Why a dedicated page (instead of a tab inside Settings)?
 * User requested clearer admin navigation in iter-70: AdminHome had two cards
 * pointing to the same Settings page. Now "Members & Roles" gets its own route.
 */
const TAB_DEFS = [
  { value: "member",    label: "Members",   icon: UserCheck, ptype: "member" },
  { value: "dep",       label: "Deps",      icon: User,      ptype: "dep" },
  { value: "inactive",  label: "Inactive",  icon: UserX,     ptype: "inactive" },
  { value: "info_only", label: "Info Only", icon: Info,      ptype: "info_only" },
];

const MembersRoles = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("member");
  const [updatingRole, setUpdatingRole] = useState(null);
  const [updatingCommittee, setUpdatingCommittee] = useState(null);
  const [updatingTreasurer, setUpdatingTreasurer] = useState(null);

  const fetchMembers = async () => {
    try {
      const res = await apiClient.get("/band/members");
      setMembers(res.data || []);
    } catch {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const toggleCommittee = async (member) => {
    setUpdatingCommittee(member.id);
    try {
      const next = !member.is_committee;
      await apiClient.put(`/band/users/${member.id}/committee?is_committee=${next}`);
      toast.success(next ? `${member.name} added to the committee mailing list` : `${member.name} removed from the committee mailing list`);
      await fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update committee status");
    } finally {
      setUpdatingCommittee(null);
    }
  };

  const toggleTreasurer = async (member) => {
    setUpdatingTreasurer(member.id);
    try {
      const next = !member.is_treasurer;
      await apiClient.put(`/band/users/${member.id}/treasurer?is_treasurer=${next}`);
      toast.success(next ? `${member.name} can now manage invoices` : `${member.name}'s treasurer access removed`);
      await fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update treasurer status");
    } finally {
      setUpdatingTreasurer(null);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      await apiClient.put(`/band/users/${userId}/role?role=${newRole}`);
      toast.success(`User ${newRole === "admin" ? "promoted to admin" : "changed to player"}`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm(
      "This will permanently delete any login account in this band that no longer has a matching player profile. It will never delete the current admin account. Continue?"
    )) return;
    try {
      const res = await apiClient.post("/band/cleanup-orphan-users");
      const n = res.data?.deleted || 0;
      toast.success(n === 0 ? "Nothing to clean up — no orphan accounts found" : `Removed ${n} orphan account${n === 1 ? "" : "s"}`);
      if (n > 0) await fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cleanup failed");
    }
  };

  // Group + alphabetize members per tab. Search filter applies across all tabs.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? members.filter((m) =>
          (m.name || "").toLowerCase().includes(q) ||
          (m.email || "").toLowerCase().includes(q)
        )
      : members;
    const buckets = { member: [], dep: [], inactive: [], info_only: [], unknown: [] };
    for (const m of filtered) {
      const pt = m.player_type || "member";
      (buckets[pt] || buckets.unknown).push(m);
    }
    Object.values(buckets).forEach((arr) =>
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    );
    return buckets;
  }, [members, search]);

  // Count badges per tab (post-search) so admins know how many are hidden by the active tab.
  const counts = TAB_DEFS.reduce((acc, t) => {
    acc[t.value] = grouped[t.ptype]?.length || 0;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading members…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="mb-2" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin home
          </Button>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Members &amp; Roles</h1>
          <p className="text-muted-foreground mt-1">
            Manage every member of <strong>{user?.band_name || "your band"}</strong> — promote admins, toggle committee &amp; treasurer access, and clean up orphan accounts.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCleanupOrphans}
          className="border-white/20 text-xs hover:bg-white/5"
          data-testid="cleanup-orphans-btn"
        >
          Clean up orphan accounts
        </Button>
      </div>

      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 bg-secondary/50"
              data-testid="members-search"
            />
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 grid-rows-2 sm:grid-cols-4 sm:grid-rows-1 h-auto gap-1 w-full">
          {TAB_DEFS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
                <Icon className="w-4 h-4 mr-2" />
                {t.label}
                <Badge variant="outline" className="ml-2 text-[10px]">{counts[t.value]}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {TAB_DEFS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="space-y-3 mt-4">
            <MemberList
              rows={grouped[t.ptype] || []}
              self={user}
              ptype={t.ptype}
              updatingRole={updatingRole}
              updatingCommittee={updatingCommittee}
              updatingTreasurer={updatingTreasurer}
              onToggleCommittee={toggleCommittee}
              onToggleTreasurer={toggleTreasurer}
              onChangeRole={updateUserRole}
            />
          </TabsContent>
        ))}
      </Tabs>

      <p className="text-xs text-muted-foreground">
        Member details (name, email, section, etc.) come from the <strong>Players</strong> page — add, edit, remove or resend welcome emails there. You cannot demote yourself or remove the last admin from the band.
      </p>
    </div>
  );
};

const MemberList = ({
  rows,
  self,
  ptype,
  updatingRole,
  updatingCommittee,
  updatingTreasurer,
  onToggleCommittee,
  onToggleTreasurer,
  onChangeRole,
}) => {
  if (rows.length === 0) {
    return (
      <Card className="glass-card">
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No {ptype === "info_only" ? "info-only contacts" : ptype + "s"} match your search.
        </CardContent>
      </Card>
    );
  }
  return rows.map((member) => {
    const hasAccount = member.has_account !== false;
    const rowKey = member.id || member.player_id;
    const isSelf = hasAccount && member.id === self?.id;
    return (
      <Card key={rowKey} className="glass-card" data-testid={`member-row-${rowKey}`}>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${member.role === "admin" ? "bg-primary/20" : "bg-secondary"}`}>
              {member.role === "admin" ? <Crown className="w-5 h-5 text-primary" /> : <User className="w-5 h-5 text-muted-foreground" />}
            </div>
            <div className="min-w-0">
              <div className="font-medium flex items-center gap-2 flex-wrap">
                {member.name}
                {isSelf && <Badge variant="outline" className="text-xs">You</Badge>}
                {!hasAccount && (
                  <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400" data-testid={`no-account-badge-${rowKey}`}>
                    No account yet
                  </Badge>
                )}
                {hasAccount && member.temp_password && (
                  <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400" data-testid={`temp-password-badge-${rowKey}`}>
                    Temp password
                  </Badge>
                )}
                {member.section_names && member.section_names.length > 0 && (
                  <span className="text-xs text-muted-foreground">· {member.section_names.join(", ")}</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">{member.email || "No email on file"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {hasAccount && (
              <Badge variant={member.role === "admin" ? "default" : "secondary"} className={member.role === "admin" ? "bg-primary/20 text-primary" : ""}>
                {member.role === "admin" ? "Admin" : "Player"}
              </Badge>
            )}
            {hasAccount && (
              <div className="flex items-center gap-2 pl-3 border-l border-white/10" data-testid={`committee-wrapper-${rowKey}`}>
                <Label htmlFor={`committee-${rowKey}`} className="text-xs text-muted-foreground cursor-pointer select-none">Committee</Label>
                <Switch id={`committee-${rowKey}`} checked={!!member.is_committee} onCheckedChange={() => onToggleCommittee(member)} disabled={updatingCommittee === member.id} data-testid={`committee-toggle-${rowKey}`} />
              </div>
            )}
            {hasAccount && (
              <div className="flex items-center gap-2 pl-3 border-l border-white/10" data-testid={`treasurer-wrapper-${rowKey}`}>
                <Label htmlFor={`treasurer-${rowKey}`} className="text-xs text-muted-foreground cursor-pointer select-none">Treasurer</Label>
                <Switch id={`treasurer-${rowKey}`} checked={!!member.is_treasurer} onCheckedChange={() => onToggleTreasurer(member)} disabled={updatingTreasurer === member.id} data-testid={`treasurer-toggle-${rowKey}`} />
              </div>
            )}
            {hasAccount && !isSelf && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onChangeRole(member.id, member.role === "admin" ? "player" : "admin")}
                disabled={updatingRole === member.id}
                className={member.role === "admin" ? "border-orange-500/30 text-orange-400 hover:bg-orange-500/10" : "border-green-500/30 text-green-400 hover:bg-green-500/10"}
                data-testid={`role-toggle-${rowKey}`}
              >
                {updatingRole === member.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : member.role === "admin" ? "Remove Admin" : "Make Admin"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  });
};

export default MembersRoles;
